import React, { useState, useEffect } from 'react';
import { formatInputValue, parseFormattedNumber } from '../utils/formatNumber';

/**
 * FormattedCurrencyInput - An input that formats numbers with thousands separators
 * while maintaining the raw numeric value for calculations
 */
export const FormattedCurrencyInput = ({ 
  value, 
  onChange, 
  placeholder = '', 
  className = '',
  prefix = '$',
  ...props 
}) => {
  const [displayValue, setDisplayValue] = useState('');
  
  // Sync display value with external value changes
  useEffect(() => {
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
    } else {
      // Format the value for display
      const num = typeof value === 'string' ? parseFormattedNumber(value) : value;
      if (num !== null && !isNaN(num)) {
        setDisplayValue(num.toLocaleString('en-US'));
      }
    }
  }, [value]);
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow empty input
    if (inputValue === '') {
      setDisplayValue('');
      onChange('');
      return;
    }
    
    // Remove non-numeric characters except for negative sign at start
    const cleanValue = inputValue.replace(/[^0-9-]/g, '');
    
    // Parse and format
    if (cleanValue === '' || cleanValue === '-') {
      setDisplayValue(cleanValue);
      onChange(cleanValue);
      return;
    }
    
    const num = parseInt(cleanValue, 10);
    if (!isNaN(num)) {
      const formatted = num.toLocaleString('en-US');
      setDisplayValue(formatted);
      onChange(formatted);
    }
  };
  
  const handleBlur = () => {
    // Clean up on blur - remove any incomplete formatting
    if (displayValue) {
      const num = parseFormattedNumber(displayValue);
      if (num !== null && !isNaN(num)) {
        setDisplayValue(num.toLocaleString('en-US'));
      }
    }
  };
  
  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${prefix ? 'pl-7' : ''} ${className}`}
        {...props}
      />
    </div>
  );
};

/**
 * FormattedNumberInput - An input that formats numbers without currency prefix
 */
export const FormattedNumberInput = ({ 
  value, 
  onChange, 
  placeholder = '', 
  className = '',
  ...props 
}) => {
  const [displayValue, setDisplayValue] = useState('');
  
  useEffect(() => {
    if (value === '' || value === null || value === undefined) {
      setDisplayValue('');
    } else {
      const num = typeof value === 'string' ? parseFormattedNumber(value) : value;
      if (num !== null && !isNaN(num)) {
        setDisplayValue(num.toLocaleString('en-US'));
      }
    }
  }, [value]);
  
  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    if (inputValue === '') {
      setDisplayValue('');
      onChange('');
      return;
    }
    
    const cleanValue = inputValue.replace(/[^0-9]/g, '');
    
    if (cleanValue === '') {
      setDisplayValue('');
      onChange('');
      return;
    }
    
    const num = parseInt(cleanValue, 10);
    if (!isNaN(num)) {
      const formatted = num.toLocaleString('en-US');
      setDisplayValue(formatted);
      onChange(formatted);
    }
  };
  
  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
};

export default FormattedCurrencyInput;
